/* eslint-disable react/jsx-filename-extension */
import { useQuery } from '@apollo/client';
import React, { createContext, useContext, useReducer, useState } from 'react';

import {
  AccessControl,
  CustomModal,
  DetailCard,
  EventEmitter,
  getTranslatedText,
  preFormatForm,
  reactSelectInlineError,
  SaveCard,
  TaskQueryBuilderForm,
  taskViewConstraints,
  validateForm
} from '@nile/components/src';
import { Checkbox, Page, TextStyle } from '@superops/ui-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  GET_ENTITY_PROPERTIES,
  GET_RULEBUILDER_OPERATORS
} from '@nile/graphql/src';
import { pick } from 'lodash';
import { GET_TASK_LISTVIEW_META } from '../../../Graphql/Mutations/ticketview';

export const TaskViewContext = createContext(null);

const TASK_DEFAULT_AVAILABLE_COLS = {
  'workItem.subject': 'taskView.workItem.subject',
  'workItem.module': 'taskView.workItem.module',
  'workItem.client.name': 'taskView.workItem.client.name',
  'workItem.site.name': 'taskView.workItem.site.name',
  createdTime: 'taskView.createdTime'
};

const ModalWrapper = styled.div`
  height: 80vh;
  width: 70vw;
`;

const AvailableColumnsWrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 20px;
`;

const LocalBadge = styled.div`
  font-size: 14px;
  font-weight: 400;
  padding: 0 10px;
  background: #e9e9e9;
`;

export function TaskViewModal({
  isOpen,
  actions,
  title,
  ...restEditProps
}: {
  isOpen: boolean;
  actions: {
    back: () => void;
    primaryAction: (arg: any) => Promise<any>;
    savePersonalization: () => Promise<any>;
  };
  title: string;
  /* Edit Task View */
  taskViewName: string;
  isSharedChecked: boolean;
  viewCondition: any;
}) {
  const [taskViewName, setTaskViewName] = useState(
    restEditProps.taskViewName ?? ''
  );
  const [isSharedChecked, setIsSharedChecked] = useState(
    restEditProps.isSharedChecked ?? false
  );
  const [submitActionLoading, setSubmitActionLoading] = useState(false);

  const [viewCondition, setViewCondition] = useReducer(
    (state, newState) => ({
      ...state,
      ...newState
    }),
    restEditProps.viewCondition
      ? { ruleCondition: restEditProps.viewCondition }
      : {
          ruleCondition: { joinOperator: 'AND', operands: [] }
        }
  );

  const [errors, setErrors] = useState({});

  const { loading: entitiesLoading, data: allEntities } = useQuery(
    GET_ENTITY_PROPERTIES
  );

  const { loading: taskListViewMetaLoading, data: taskListViewMeta } = useQuery(
    GET_TASK_LISTVIEW_META
  );

  const { loading: operatorsLoading, data: allOperators } = useQuery(
    GET_RULEBUILDER_OPERATORS
  );

  const taskAttributes = allEntities?.getEntitiesMeta.find((item) => {
    return item.name === 'Task';
  }).attributes;

  const defaultSelectedColumns =
    (!taskListViewMetaLoading &&
      taskListViewMeta?.getTaskListViewMetaInfo?.defaultSelectedAttributes.map(
        (el) => {
          const selectedAttr = taskAttributes?.find((attr) => attr.name === el);

          if (!selectedAttr || el === 'createdTime') {
            // To Handle for workItem.xx ids
            // TODO: Add lokalise keys for workItem
            return {
              id: el,
              // label: el,
              labelI18nKey: TASK_DEFAULT_AVAILABLE_COLS[el] ?? el
            };
          }
          return {
            id: selectedAttr.name,
            // label: getTranslatedText(intl, selectedAttr.label),
            labelI18nKey: selectedAttr.label
          };
        }
      )) ||
    [];

  function handleCreateOrUpdateViewSave() {
    const input = {
      name: taskViewName,
      viewCondition: viewCondition.ruleCondition,
      shared: isSharedChecked,
      module: 'TASK'
    };

    const dummyForm = pick(input, ['name', 'shared']);
    // eslint-disable-next-line no-underscore-dangle
    const _dummyForm = preFormatForm(dummyForm);
    const validationResult = validateForm(
      { ..._dummyForm, viewCondition: viewCondition.ruleCondition },
      taskViewConstraints
    );

    if (validationResult) {
      return setErrors(validationResult);
    }

    setErrors({});

    setSubmitActionLoading(true);
    actions.primaryAction(input).finally(() => {
      setSubmitActionLoading(false);
      actions.back();
      EventEmitter.dispatch('refreshTaskList');
    });
  }

  return (
    <CustomModal open={isOpen} onClose={actions.back}>
      <TaskViewContext.Provider
        value={{
          defaultSelectedColumns,
          loading: {
            entities: entitiesLoading,
            taskListViewMeta: taskListViewMetaLoading,
            operators: operatorsLoading
          },
          taskViewName,
          setTaskViewName,
          isSharedChecked,
          setIsSharedChecked,
          onBack: actions.back,
          taskListViewMeta,
          allEntities,
          allOperators,
          setViewCondition,
          viewCondition,
          errors
        }}
      >
        <ModalWrapper>
          <Page
            variant="primary"
            title={title}
            primaryAction={
              <SaveCard
                handleSave={handleCreateOrUpdateViewSave}
                actions={actions}
                loading={submitActionLoading}
                removeSecondaryBtnBg
              />
            }
            contentHeight="100px"
          >
            <TaskViewForm />
          </Page>
        </ModalWrapper>
      </TaskViewContext.Provider>
    </CustomModal>
  );
}

function TaskViewForm() {
  const intl = useIntl();
  const {
    taskViewName,
    setTaskViewName,
    isSharedChecked,
    setIsSharedChecked,
    onBack,
    loading,
    taskListViewMeta,
    allEntities,
    allOperators,
    setViewCondition,
    viewCondition,
    defaultSelectedColumns,
    errors
  } = useContext(TaskViewContext);

  const COLUMNS_TO_OMIT = ['workItem.module', 'createdTime']; // OMIT These columns as they are not availabe in list view
  const availableColumns = defaultSelectedColumns.filter(
    (el) => !COLUMNS_TO_OMIT.includes(el?.id)
  ); // These columns will only be available in list view

  return (
    <>
      <DetailCard
        actions={{
          updateName: setTaskViewName,
          back: onBack
        }}
        disableActive
        noDesciption
        notSectioned
        data={{ name: taskViewName }}
        errors={errors?.name}
        noCard
      >
        <AccessControl
          roleToCheck="taskSharedViews.allow"
          render={
            <div className="mt-20">
              <Checkbox
                label={getTranslatedText(
                  intl,
                  'task.newView.checkbox.sharedView'
                )}
                checked={isSharedChecked}
                onChange={setIsSharedChecked}
              />
            </div>
          }
        />
      </DetailCard>

      <div className="mt40 mb-40">
        {!loading.taskListViewMeta && !loading.entities && !loading.operators && (
          <TaskQueryBuilderForm
            meta={taskListViewMeta.getTaskListViewMetaInfo}
            allEntities={allEntities}
            allOperators={allOperators}
            context={{
              updateAutomationRule: (formattedRule, key) => {
                setViewCondition({ [key]: formattedRule });
              }
            }}
            query={viewCondition.ruleCondition}
            errors={errors?.['viewCondition.operands']}
          />
        )}
        {typeof errors?.['viewCondition.operands']?.[0] === 'string' &&
          reactSelectInlineError(errors?.['viewCondition.operands'])}
      </div>

      <TextStyle variation="strong">
        <FormattedMessage id="taskView.availableColumns" />
      </TextStyle>
      {!loading.entities && (
        <AvailableColumnsWrapper>
          {availableColumns.map(({ id, labelI18nKey }) => {
            return (
              <LocalBadge key={id}>
                {labelI18nKey ? <FormattedMessage id={labelI18nKey} /> : null}
              </LocalBadge>
            );
          })}
        </AvailableColumnsWrapper>
      )}
    </>
  );
}
